import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, ImagePlus, Loader2, Router as RouterIcon, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { extractApiError } from '@/utils/apiError';
import { PhoneFrame } from '@/components/reviews/PhoneFrame';
import { CaptivePortalView, slotOf } from './CaptivePortalView';
import {
  captiveUrl, deleteCaptiveOffer, pushCaptiveToRouter, putCaptivePortal, uploadCaptiveOffer,
  type CaptivePortalDto, type CaptiveSettingsDto, type CaptiveSlot, type CaptiveTexts,
  type TextSize, type TextWeight,
} from '@/api/captive';

const SLOTS: { key: CaptiveSlot; label: string; hint: string }[] = [
  { key: 'title', label: 'Заголовок', hint: 'Обычно название филиала' },
  { key: 'subtitle', label: 'Подзаголовок', hint: 'Например «Гостевой Wi-Fi»' },
  { key: 'phone_prompt', label: 'Над полем телефона', hint: 'Введите номер телефона' },
  { key: 'legal', label: 'Про закон', hint: 'Зачем просим номер' },
  { key: 'code_prompt', label: 'Над полем кода', hint: 'Введите код из СМС' },
  { key: 'success_title', label: 'Заголовок после входа', hint: 'Интернет включён' },
  { key: 'success_text', label: 'Текст после входа', hint: 'Хорошего дня' },
  { key: 'button', label: 'Кнопка', hint: 'Продолжить' },
];
const SIZES: TextSize[] = ['sm', 'md', 'lg', 'xl'];
const WEIGHTS: { key: TextWeight; label: string }[] = [
  { key: 'thin', label: 'Тонкий' }, { key: 'normal', label: 'Обычный' },
  { key: 'bold', label: 'Жирный' }, { key: 'caps', label: 'КАПСОМ' },
];
const STEPS: { key: 'phone' | 'code' | 'done'; label: string }[] = [
  { key: 'phone', label: 'Телефон' }, { key: 'code', label: 'Код' }, { key: 'done', label: 'Доступ открыт' },
];

export function CaptivePortalEditor({ zoneId, settings, onClose, onSaved }: {
  zoneId: number | string;
  settings: CaptiveSettingsDto;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const portal = settings.portal as CaptivePortalDto;
  const [texts, setTexts] = useState<CaptiveTexts>(() => {
    const out: CaptiveTexts = {};
    for (const s of SLOTS) out[s.key] = slotOf(portal.texts, s.key);
    if (!out.title?.text) out.title = { ...slotOf(portal.texts, 'title'), text: settings.branch_name };
    return out;
  });
  const [identity, setIdentity] = useState(portal.identity);
  const [active, setActive] = useState(portal.is_active);
  const [offers, setOffers] = useState(portal.offers);
  const [step, setStep] = useState<'phone' | 'code' | 'done'>('phone');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const set = (key: CaptiveSlot, patch: Partial<{ text: string; size: TextSize; weight: TextWeight }>) =>
    setTexts((prev) => ({ ...prev, [key]: { ...slotOf(prev, key), ...patch } }));

  async function save() {
    setSaving(true); setError(null);
    try {
      await putCaptivePortal(zoneId, { texts, identity, is_active: active });
      await onSaved();
      onClose();
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить'));
    } finally { setSaving(false); }
  }

  async function addOffer(file: File) {
    setBusy(true); setError(null);
    try {
      const resp = await uploadCaptiveOffer(zoneId, file);
      setOffers(resp.data.portal.offers);
    } catch (e) { setError(extractApiError(e, 'Не удалось загрузить картинку')); }
    finally { setBusy(false); }
  }
  async function removeOffer(id: number) {
    setBusy(true);
    try {
      const resp = await deleteCaptiveOffer(zoneId, id);
      setOffers(resp.data.portal.offers);
    } catch (e) { setError(extractApiError(e, 'Не удалось удалить')); }
    finally { setBusy(false); }
  }

  const url = captiveUrl(portal.public_id);
  const router = portal.router;
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function push() {
    setPushing(true);
    setPushMsg(null);
    try {
      await pushCaptiveToRouter(zoneId);
      await onSaved();
      setPushMsg({ ok: true, text: 'Роутер настроен: гости будут попадать на эту страницу.' });
    } catch (e) {
      // Молча падать тут нельзя: человек нажал кнопку и должен узнать, что чинить.
      setPushMsg({ ok: false, text: extractApiError(e, 'Роутер не ответил — проверьте адрес и пароль в карточке оборудования.') });
    } finally {
      setPushing(false);
    }
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6" data-overlay>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" className="relative z-10 flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold">Гостевой портал Wi-Fi</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted" aria-label="Закрыть">
            <X className="size-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5 md:flex-row">
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={active} onCheckedChange={setActive} /> Портал включён
              </label>
              {(['linza', 'surf'] as const).map((idt) => (
                <button key={idt} type="button" onClick={() => setIdentity(idt)}
                  className={cn('rounded-lg border px-3 py-1.5 text-sm', identity === idt ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted')}>
                  {idt === 'linza' ? 'Linza' : 'Surf Coffee'}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <Input readOnly value={url} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
              <Button variant="outline" onClick={() => { void navigator.clipboard.writeText(url); setCopied(true); window.setTimeout(() => setCopied(false), 1500); }}>
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">Перехват на роутере</div>
                <Button variant="outline" size="sm" disabled={!router.can_push || pushing} onClick={() => void push()}>
                  {pushing ? <Loader2 className="size-4 animate-spin" /> : <RouterIcon className="size-4" />}
                  <span className="ml-2">Отправить конфиг на роутер</span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Роутер начнёт перехватывать гостей на гостевой сети и вести на эту страницу.
                Без перехвата она тоже работает — по ссылке или QR-коду рядом с кассой.
              </p>
              {router.blockers.length > 0 && (
                <ul className="list-inside list-disc text-xs text-amber-700 dark:text-amber-300">
                  {router.blockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              )}
              {router.pushed_at && !pushMsg && (
                <p className="text-xs text-muted-foreground">
                  Отправлено {new Date(router.pushed_at).toLocaleString('ru-RU')}
                </p>
              )}
              {router.push_error && !pushMsg && (
                <p className="text-xs text-destructive">Прошлая попытка: {router.push_error}</p>
              )}
              {pushMsg && (
                <p className={cn('text-xs', pushMsg.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive')}>
                  {pushMsg.text}
                </p>
              )}
            </div>

            {SLOTS.map((s) => {
              const v = slotOf(texts, s.key);
              return (
                <div key={s.key} className="space-y-1">
                  <div className="text-sm font-medium">{s.label}</div>
                  <Input value={v.text} placeholder={s.hint} className="text-sm"
                    onChange={(e) => set(s.key, { text: e.target.value.slice(0, 300) })} />
                  <div className="flex flex-wrap gap-1.5">
                    <div className="inline-flex overflow-hidden rounded-md border border-border">
                      {SIZES.map((size) => (
                        <button key={size} type="button" onClick={() => set(s.key, { size })}
                          className={cn('h-7 w-8 text-[11px]', v.size === size ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>
                          {size.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <div className="inline-flex overflow-hidden rounded-md border border-border">
                      {WEIGHTS.map((w) => (
                        <button key={w.key} type="button" onClick={() => set(s.key, { weight: w.key })}
                          className={cn('h-7 px-2 text-[11px]', v.weight === w.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted')}>
                          {w.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Спецпредложения</div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void addOffer(f); }} />
                <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
                  {busy ? <Loader2 className="size-3.5 animate-spin" /> : <ImagePlus className="size-3.5" />} Добавить фото
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Листаются сами каждые 5 секунд, гость может пролистать пальцем.</p>
              <div className="flex flex-wrap gap-2">
                {offers.map((o) => (
                  <div key={o.id} className="group relative">
                    <img src={`${o.url.startsWith('http') ? '' : ''}${o.url}`} alt="" className="size-16 rounded object-cover" />
                    <button type="button" onClick={() => void removeOffer(o.id)}
                      className="absolute -right-1 -top-1 rounded-full bg-background p-0.5 shadow" aria-label="Удалить">
                      <Trash2 className="size-3.5 text-destructive" />
                    </button>
                  </div>
                ))}
                {offers.length === 0 && <p className="text-xs text-muted-foreground">Пока пусто.</p>}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div className="flex shrink-0 flex-col items-center">
            <div className="mb-2 flex gap-1">
              {STEPS.map((s) => (
                <button key={s.key} type="button" onClick={() => setStep(s.key)}
                  className={cn('rounded-full px-2.5 py-1 text-xs', step === s.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                  {s.label}
                </button>
              ))}
            </div>
            <PhoneFrame scale={0.78}>
              <CaptivePortalView identity={identity} branchName={settings.branch_name} texts={texts} offers={offers} forceStep={step} />
            </PhoneFrame>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" />} Сохранить
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
